import { IRunnable, t_element_lookup_list, t_key, IIndexable } from 'core-ts-types';
import { IDatasource, Datasource } from './datasource';
import { getElement, DB } from '../core/db_utilities';
import * as _4 from 'lodash';

type meta_calculation<T> = (t: T) => any;
type mapIterator<V1, V2> = (v1: V1, v2: V2) => V2 | undefined;

interface IRecordSource<T> {
  get(k: t_key | string): T | undefined;
}

interface IDefinitionSingle<T, M>{
  calculation: meta_calculation<T & Partial<M>>;
  attribute: keyof M;
  resultType: 'single';
}
interface IDefinitionMultiple<T, M>{
  calculation: meta_calculation<T & Partial<M>>;
  resultType: 'multiple';
}
type IDefinition<T, M> = IDefinitionSingle<T, M> | IDefinitionMultiple<T, M>;

export interface IMetasourceConstructor<T extends IIndexable, M extends IIndexable> {
  new(source: IDatasource<T>): IMetasource<T, M>;
}

export interface IMetasource<T extends IIndexable, M extends IIndexable> extends IRunnable {
  includeManyToMany<U>(attribute: keyof M, lookupSource: IRecordSource<U>, computeAttribute: keyof T, iterator?: mapIterator<T & Partial<M>, U>): this;
  includeOneToMany<U>(attribute: keyof M, lookupSource: IRecordSource<U>, computeAttribute: keyof T, iterator?: mapIterator<T & Partial<M>, U>): this;
  includeAttribute<K extends keyof M>(attribute: K, calculation: (t: T & Partial<M>) => M[K]): this;
  includeAttributes(calculation: (entry: T & Partial<M>) => Partial<M>): this;
  cursor<MT extends (T & M)>(elements: (T[] | Record<string, T>), fn?: (o: MT) => Partial<MT> | undefined): MT[];
  derived<MT extends (T & M)>(fn?: (o: MT) => Partial<MT> | undefined): MT[];
  slice(lookup: t_element_lookup_list<T>): M[];
  query(qry: (t: M) => boolean): M[];
  compose<MT extends (T & M)>(source: T | t_key | string): MT;
  composes<MT extends (T & M)>(sources: T[]): MT[];
  get(source: T | t_key | string): M | undefined;
  read(): DB<M>;
  clear(): this;
  run(): this;
}

export class Metasource<T extends IIndexable, M extends IIndexable> implements IMetasource<T, M> {

  protected _db: DB<M> = {};
  protected _definitions: IDefinition<T, M>[] = [];

  constructor(private __source: Datasource<T>) {

  }

  /**
   * erase all meta objects
   * @deprecated this component auto-writes itself
   */
  clear(): this {
    this._db = {};
    return this;
  }

  /**
   * get entire meta set
   */
  read(): DB<M> {
    return this._db;
  }

  /**
   * @param k
   */
  get(source: T | t_key | string): M | undefined {
    return (!!source['id'])
      ? this._db[source['id']]
      : this._db[source as string];
  }

  /**
   * @param source an element from the datasource
   */
  compose<MT extends (T & M)>(source: T | t_key | string): MT {
    const sourceObject = !!source['id'] ? source : this.__source.get(source as t_key);
    return {
      ...(this.get(source)) as Object,
      ...sourceObject as Object
    } as MT;
  }

  /**
   * @param source an element from the datasource
   */
  composes<MT extends (T & M)>(sources: T[]): MT[] {
    return _4.map(sources, (o) => {
      return this.compose(o);
    });
  }

  /**
   * extract a set of meta-objects to match datasource elements
   * @param lookup an array of T or keys of Datasource<T>
   */
  slice(lookup: t_element_lookup_list<T>): M[] {
    return _4.compact(_4.map(lookup, (o: t_key | T) => getElement(this._db, o)) as any) as M[];
  }

  /**
   * mix all meta objects in to a each of the datasource elements
   * @param elements optional elements or the entire datasoure
   */
  derived<MT extends (T & M)>(fn?: (o: MT) => Partial<MT> | undefined): MT[] {
    return this.cursor(this.__source.read(), fn);
  }

  /**
   * mix all meta objects in to a subset of the datasource elements
   * @param elements optional elements or the entire datasoure
   */
  cursor<MT extends (T & M)>(elements: (T[] | Record<string, T>), fn?: (o: MT) => Partial<MT> | undefined): MT[] {
    return _4.compact(_4.map(_4.map(elements as T[], (element: T) => this.compose(element)), fn) as MT[]);
  }

  /**
   * return a filtered set
   * @param qry
   */
  query<U extends IIndexable>(qry: (t: M) => boolean = (_0) => true, iterator?: (t: M) => U): U[] {
    return  _4.map(_4.filter(this._db, qry), iterator);
  }

  /**
   *
   * @param attribute key on all elements
   * @param calculation calculation of the key values
   */
  includeAttribute<K extends keyof M>(attribute: K, calculation: (t: T & Partial<M>) => M[K]): this {
    this._definitions.push({
      attribute,
      calculation,
      resultType: 'single'
    });

    return this;
  }

  /**
   *
   * @param calculation calculation of multiple metasource key values
   */
  includeAttributes(calculation: (element: T & Partial<M>) => Partial<M>): this {
    this._definitions.push({
      calculation,
      resultType: 'multiple'
    });
    return this;
  }

  /**
   * @pre d1 has a property with an array of id's. d2 has the id reference
   * @post d1 has a new property with an underscore prefix of the objects, that exist
   * @param computeAttribute array of id keys mapped to d2
   */
  includeManyToMany<U>(attribute: keyof M, lookupSource: IRecordSource<U>, computeAttribute: keyof T, iterator: mapIterator<T & Partial<M>, U> = (_0, v2) => v2): this {
    this._definitions.push({
      attribute,
      calculation: (entry) => {
        const v1: t_key[] = entry[computeAttribute] as any;

        if (!v1) {
          return undefined;
        }

        return _4.compact(_4.map(_4.compact(_4.map(v1, (id: string) => {
          return lookupSource.get(id);
        })), (u: U) => {
          return iterator(entry, u);
        }));
      },
      resultType: 'single'
    });

    return this;
  }

  /**
   *
   * @pre d1 has a property with an array of id's. d2 has the id reference
   * @param attribute array of id keys mapped to d2
   */
  includeOneToMany<U>(attribute: keyof M, lookupSource: IRecordSource<U>, computeAttribute: keyof T, iterator: mapIterator<T & Partial<M>, U> = (_0, v2) => v2): this {
    this._definitions.push({
      attribute,
      calculation:  (entry) => {
        const v1: t_key = entry[computeAttribute] as any;
        const v2 = lookupSource.get(v1);

        return (v1 && v2)
          ? iterator(entry, v2)
          : undefined;
      },
      resultType: 'single'
    });

    return this;
  }

  /**
   * calculate all elements
   */
  run(): this {
    this.clear();

    _4.each(this.__source.read(), (datasourceEntry: T) => {
      const entryMetasource = this._db[datasourceEntry.id] = {
        id: datasourceEntry.id
      } as M;

      const fullEntry = Object.assign({}, datasourceEntry, entryMetasource);
      const updateMetaSourceProps = (entry: T & M & {}, definition: IDefinition<T, M>) => {
        return (definition.resultType === 'multiple') ? definition.calculation(entry) : { [definition.attribute] : definition.calculation(entry) };
      };
      _4.each(this._definitions, (definition) => {
        _4.merge(fullEntry, updateMetaSourceProps(fullEntry, definition));
        _4.merge(entryMetasource, updateMetaSourceProps(fullEntry, definition));
      });
    });

    return this;
  }

}



// WEBPACK FOOTER //
// ./app/vendor/utility-ts-orm/dist/types/metasource.ts